import { enumPresentation } from "@/lib/enum";
import type { RiskPolicy } from "@gram/client/models/components/riskpolicy.js";
import {
  DETECTION_RULES,
  ruleCategoryMeta,
  type DetectorMode,
  type RuleCategory,
} from "./policy-data";
import { PRESIDIO_CATEGORIES } from "./policy-form";
import { ruleIdToPresidioEntity } from "./rule-ids";

const DELETE_RULE_LIST_LIMIT = 4;

const CATEGORY_LEVEL_SOURCE_BY_CATEGORY: Partial<Record<RuleCategory, string>> =
  {
    prompt_injection: "prompt_injection",
    shadow_mcp: "shadow_mcp",
    destructive_tool: "destructive_tool",
    cli_destructive: "cli_destructive",
  };

function hasEnabledVisibleRule(
  category: RuleCategory,
  disabledRules: Set<string>,
): boolean {
  return DETECTION_RULES[category].some(
    (rule) => !rule.hidden && !disabledRules.has(rule.id),
  );
}

function presidioCategories(
  policy: RiskPolicy,
  mode: DetectorMode,
): RuleCategory[] {
  if (!policy.sources.includes("presidio")) {
    return [];
  }
  // The LLM analyzer ignores the entity list and decides the personal-data
  // category per finding, so the policy enforces one PII group.
  if (mode === "llm") {
    return ["pii"];
  }

  const disabledRules = new Set(policy.disabledRules ?? []);
  const entitySet = new Set(policy.presidioEntities ?? []);
  if (entitySet.size === 0) {
    return PRESIDIO_CATEGORIES.filter((category) =>
      hasEnabledVisibleRule(category, disabledRules),
    );
  }

  return PRESIDIO_CATEGORIES.filter((category) =>
    DETECTION_RULES[category].some(
      (rule) =>
        !rule.hidden &&
        !disabledRules.has(rule.id) &&
        entitySet.has(ruleIdToPresidioEntity(rule.id)),
    ),
  );
}

export function getPolicyRuleGroupNamesForDeleteDialog(
  policy: RiskPolicy,
  mode: DetectorMode = "presidio",
): string[] {
  if (policy.policyType === "prompt_based") {
    return [];
  }

  const categories: RuleCategory[] = [];
  const disabledRules = new Set(policy.disabledRules ?? []);

  if (
    policy.sources.includes("gitleaks") &&
    hasEnabledVisibleRule("secrets", disabledRules)
  ) {
    categories.push("secrets");
  }

  categories.push(...presidioCategories(policy, mode));

  for (const [category, source] of Object.entries(
    CATEGORY_LEVEL_SOURCE_BY_CATEGORY,
  ) as Array<[RuleCategory, string]>) {
    if (policy.sources.includes(source)) {
      categories.push(category);
    }
  }

  if (policy.customRuleIds?.length) {
    categories.push("custom");
  }

  return categories.map((category) => ruleCategoryMeta(category, mode).label);
}

export function getPolicyDeleteRuleActionLabel(policy: RiskPolicy): string {
  const labels: Record<RiskPolicy["action"], string> = {
    flag: "flag",
    warn: "warn",
    block: "block",
    quarantine: "quarantine",
  };
  return enumPresentation(labels, policy.action, "unknown");
}

export function getPolicyDeleteImpactText(
  policy: RiskPolicy,
  hasRuleGroups: boolean,
): string {
  const action = getPolicyDeleteRuleActionLabel(policy);
  if (policy.action === "quarantine") {
    return "This policy will stop opening new session quarantines. Existing quarantines remain active until an admin releases them.";
  }
  if (hasRuleGroups) {
    return `The following ${action} rules will no longer be enforced.`;
  }

  return `Any ${action} action this policy applies will stop immediately.`;
}

export function getPolicyDeleteRuleListItems(ruleNames: string[]): string[] {
  if (ruleNames.length <= DELETE_RULE_LIST_LIMIT) {
    return ruleNames;
  }

  return [
    ...ruleNames.slice(0, DELETE_RULE_LIST_LIMIT),
    `and ${ruleNames.length - DELETE_RULE_LIST_LIMIT} more`,
  ];
}
